import { Injectable, computed, signal } from '@angular/core';
import { AuthState, User, Tenant, AuthMethods } from './auth.models';
import { isITStaffEmail } from '../config/it-staff-domains';

const INITIAL_STATE: AuthState = {
  isAuthenticated: false,
  requiresTwoFactor: false,
  pendingEmail: undefined,
  user: null,
  tenants: [],
  methods: null,
  loading: false,
  error: null
};

@Injectable({
  providedIn: 'root'
})
export class AuthStateService {
  private readonly stateSignal = signal<AuthState>(INITIAL_STATE);

  readonly state = this.stateSignal.asReadonly();
  readonly isAuthenticated = computed(() => this.stateSignal().isAuthenticated);
  readonly requiresTwoFactor = computed(() => this.stateSignal().requiresTwoFactor);
  readonly currentUser = computed(() => this.stateSignal().user);
  readonly userEmail = computed(() => this.stateSignal().user?.email ?? '');
  readonly userTenant = computed(() => this.stateSignal().user?.tenant ?? '');
  readonly userRole = computed(() => this.stateSignal().user?.role ?? '');
  readonly authMethods = computed(() => this.stateSignal().methods);
  readonly isLoading = computed(() => this.stateSignal().loading);
  readonly error = computed(() => this.stateSignal().error);

  /** True for Irish Taylor staff (staff tenant or @irishtaylor.com email).
   *  Controls Approve & Publish / Rollback — not the Admin Panel route itself. */
  readonly isITStaff = computed(() => {
    const user = this.stateSignal().user;
    if (!user) return false;
    // Matches backend identity.is_staff_tenant default ("Irish Taylor").
    const staffTenant = (user.tenant || '').trim().toLowerCase() === 'irish taylor';
    return staffTenant || isITStaffEmail(user.email);
  });

  /** @deprecated Use isITStaff instead. Kept for backwards compatibility. */
  readonly isIrishTaylor = this.isITStaff;

  setAuthenticated(user: User): void {
    this.stateSignal.update((s) => ({
      ...s,
      isAuthenticated: true,
      requiresTwoFactor: false,
      user,
      pendingEmail: undefined,
      tenants: [],
      loading: false,
      error: null
    }));
  }

  setRequiresTwoFactor(email: string): void {
    this.stateSignal.update((s) => ({
      ...s,
      isAuthenticated: false,
      requiresTwoFactor: true,
      pendingEmail: email,
      loading: false,
      error: null
    }));
  }

  setTenants(tenants: Tenant[]): void {
    this.stateSignal.update((s) => ({
      ...s,
      tenants
    }));
  }

  setMethods(methods: AuthMethods): void {
    this.stateSignal.update((s) => ({
      ...s,
      methods
    }));
  }

  setLoading(loading: boolean): void {
    this.stateSignal.update((s) => ({ ...s, loading }));
  }

  setError(error: string | null): void {
    this.stateSignal.update((s) => ({ ...s, error, loading: false }));
  }

  clear(): void {
    this.stateSignal.update((s) => ({
      ...INITIAL_STATE,
      methods: s.methods
    }));
  }
}
